import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { BrandMark } from "../components/BrandMark";
import { KeyRound } from "lucide-react";

// Force-change screen shown when a temporary password is on file (`must_change=true`).
export default function ChangePasswordFirst() {
    const { user, setUser, logout } = useAuth();
    const nav = useNavigate();
    const [current, setCurrent] = useState("");
    const [next1, setNext1] = useState("");
    const [next2, setNext2] = useState("");
    const [busy, setBusy] = useState(false);

    async function onSubmit(e) {
        e.preventDefault();
        if (next1.length < 6) return toast.error("New password must be at least 6 characters");
        if (next1 !== next2) return toast.error("New passwords do not match");
        if (next1 === current) return toast.error("New password must be different from the temporary one");
        setBusy(true);
        try {
            await api.post("/users/me/change-password", {
                current_password: current,
                new_password: next1,
            });
            // Refresh /auth/me so must_change flips to false and ProtectedRoute unlocks the app.
            const { data } = await api.get("/auth/me");
            setUser(data);
            toast.success("Password updated. You're all set.");
            nav("/", { replace: true });
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
            <Card className="w-full max-w-md p-8 rounded-2xl border-slate-100 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                    <BrandMark size={40} />
                </div>
                <div className="flex items-center gap-2 text-blue-900 mb-1">
                    <KeyRound className="h-5 w-5" strokeWidth={1.75} />
                    <span className="text-xs uppercase tracking-widest font-semibold">
                        Set your password
                    </span>
                </div>
                <h2 className="font-display text-2xl font-bold text-slate-900">
                    Change your temporary password
                </h2>
                <p className="text-sm text-slate-500 mt-1.5">
                    Hi {user?.name || user?.username}, please pick a new password before continuing.
                </p>

                <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="force-change-form">
                    <div className="space-y-1.5">
                        <Label>Temporary password</Label>
                        <Input
                            type="password"
                            data-testid="force-change-current"
                            value={current}
                            onChange={(e) => setCurrent(e.target.value)}
                            className="h-11 rounded-xl"
                            autoComplete="current-password"
                            required
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label>New password</Label>
                        <Input
                            type="password"
                            data-testid="force-change-new"
                            value={next1}
                            onChange={(e) => setNext1(e.target.value)}
                            className="h-11 rounded-xl"
                            autoComplete="new-password"
                            required
                            minLength={6}
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label>Confirm new password</Label>
                        <Input
                            type="password"
                            data-testid="force-change-confirm"
                            value={next2}
                            onChange={(e) => setNext2(e.target.value)}
                            className="h-11 rounded-xl"
                            autoComplete="new-password"
                            required
                            minLength={6}
                        />
                    </div>
                    <Button
                        type="submit"
                        disabled={busy}
                        data-testid="force-change-submit"
                        className="w-full h-12 rounded-xl bg-blue-900 hover:bg-blue-800 text-white font-semibold tap"
                    >
                        {busy ? "Saving…" : "Update password"}
                    </Button>
                    <button
                        type="button"
                        onClick={() => { logout(); nav("/login", { replace: true }); }}
                        data-testid="force-change-signout"
                        className="w-full text-center text-xs text-slate-500 hover:text-slate-700"
                    >
                        Sign out instead
                    </button>
                </form>
            </Card>
        </div>
    );
}
